import type { ReactNode } from 'react';

export function Koma({ x, y, label, opponent = false }: { x: number; y: number; label: string; opponent?: boolean }) {
  return <g transform={'translate(' + x + ' ' + y + ')' + (opponent ? ' rotate(180)' : '')}>
    <path d="M0 -12L9 -7L11 12H-11L-9 -7Z" fill="#ecddbf" stroke="#8a7b61" />
    <text x="0" y="6" fontSize="16" textAnchor="middle">{label}</text>
  </g>;
}

export function ChuSmallGrid() {
  return <g stroke="#c5c1b4">{[0,1,2,3].map(i=><g key={i}><path d={'M2 '+(2+i*24)+'H74'} /><path d={'M'+(2+i*24)+' 2V74'} /></g>)}</g>;
}

type DiagramPiece = { row: number; col: number; label: string; opponent?: boolean };
const own = (row: number, col = 5): DiagramPiece => ({ row, col, label: '獅' });
const enemy = (row: number, col: number, label: string): DiagramPiece => ({ row, col, label, opponent: true });
function Board({ pieces, minRow = 5, maxRow = 7, minCol = 4, maxCol = 6, children }: {
  pieces: DiagramPiece[]; minRow?: number; maxRow?: number; minCol?: number; maxCol?: number; children?: ReactNode;
}) {
  const rows=maxRow-minRow+1, cols=maxCol-minCol+1;
  return <g data-chu-board data-cell-size="24" data-min-row={minRow} data-max-row={maxRow} data-min-col={minCol} data-max-col={maxCol}>
    <g stroke="#c5c1b4">
      {Array.from({length:rows+1},(_,i)=><path key={'r'+i} d={'M2 '+(2+i*24)+'H'+(2+cols*24)} />)}
      {Array.from({length:cols+1},(_,i)=><path key={'c'+i} d={'M'+(2+i*24)+' 2V'+(2+rows*24)} />)}
    </g>
    {children}
    {pieces.map(p=><g key={p.row+','+p.col} data-square={p.row+','+p.col} data-piece={p.label} data-owner={p.opponent?'Second':'First'}><Koma x={14+(p.col-minCol)*24} y={14+(maxRow-p.row)*24} label={p.label} opponent={p.opponent} /></g>)}
  </g>;
}

export function LionReach() {
  return <svg viewBox="0 0 308 136" role="img" aria-label="獅子を中心とした5×5の範囲です。外周16マスの点へは、間の駒を飛び越して1回で跳べます。自分の駒がいるマスには止まれません。" data-chu-diagram="reach">
    <Board minRow={3} maxRow={7} minCol={3} maxCol={7} pieces={[own(5)]}>
      {Array.from({length:5},(_,r)=>Array.from({length:5},(_,c)=>Math.max(Math.abs(r-2),Math.abs(c-2))===2?<circle key={r+','+c} data-jump-target cx={14+c*24} cy={14+r*24} r="4" fill="#65785d" />:null))}
    </Board>
    <text x="154" y="58" fontSize="14">自分の駒の上には<tspan x="154" dy="22">止まれません。</tspan></text>
  </svg>;
}

export function LionReturn({ capture }: { capture: boolean }) {
  return <svg viewBox="0 0 308 94" role="img" aria-label={capture?'獅子が隣の銀を取り、2回目に元のマスへ戻ります。前後で獅子の位置は同じで、相手の銀だけがなくなります。':'獅子が隣の空いたマスへ動き、2回目に元のマスへ戻ります。盤面は同じで、手番が相手へ移ります。'} data-chu-diagram={capture?'igui':'jitto'}>
    {[0,1,2].map(step=><g key={step} data-frame={step} transform={'translate('+(step*106+10)+' 0)'}>
      <Board pieces={[own(step===1?6:5),...(capture&&step===0?[enemy(6,5,'銀')]:[])]} />
      <text x="38" y="90" fontSize="13" textAnchor="middle">{['動く前','1回目','2回目'][step]}</text>
    </g>)}
  </svg>;
}

export function LionGuarded() {
  return <svg viewBox="0 0 308 151" role="img" aria-label="通常の獅子どうしの取り合いです。隣の獅子は金に守られていても取れます。2マス先で金に守られている獅子は取れません。金の守りがなければ取れます。3例とも同じ4段3列の範囲と尺度です。" data-chu-diagram="guarded">
    {[0,1,2].map(i=><g key={i} data-frame={i} transform={'translate('+(i*106+10)+' 22)'}>
      <text x="38" y="-8" textAnchor="middle" fontSize="13">{i===0?'隣':'2マス先'}</text>
      <Board maxRow={8} pieces={[own(5),enemy(i===0?6:7,5,'獅'),...(i<2?[enemy(i===0?7:8,5,'金')]:[])]}>
        {i<2&&<path d={i===0?'M51 38Q73 50 51 62m5 -5l-5 5 7 0':'M51 14Q73 26 51 38m5 -5l-5 5 7 0'} fill="none" stroke="#65785d" strokeWidth="1.4" data-guard />}
      </Board>
      <text x="38" y="124" textAnchor="middle" fontSize="13">{i===1?'取れません。':'取れます。'}</text>
    </g>)}
  </svg>;
}

export function LionHiddenGuard() {
  return <svg viewBox="0 0 308 170" role="img" aria-label="自分の獅子が相手の飛車の道をふさいでいます。仮に2マス先の獅子を取ると飛車の道が開き、そのマスへ飛車が届きます。この獅子は守られているので取れません。右は合法手ではなく仮の盤面です。" data-chu-diagram="hidden-guard">
    {[0,1].map(after=><g key={after} data-frame={after} transform={'translate('+(after*154+40)+' 22)'}>
      <text x="38" y="-8" textAnchor="middle" fontSize="13">{after?'仮に取ると':'取る前'}</text>
      <Board minRow={3} maxRow={7} pieces={[own(after?7:5),enemy(3,5,'飛'),...(!after?[enemy(7,5,'獅')]:[])]}>
        <path d={after?'M38 98V26m-4 5l4 -5 4 5':'M38 98V76m-4 5l4 -5 4 5'} fill="none" stroke="#65785d" strokeWidth="1.4" data-guard />
        {Boolean(after)&&<circle cx="38" cy="62" r="11" fill="none" stroke="#a5ad9b" strokeDasharray="3 3" />}
      </Board>
    </g>)}
    <text x="154" y="166" textAnchor="middle" fontSize="13">飛車の道が開くので、取れません。</text>
  </svg>;
}

export function LionAttachment({ doubleLion = false }: { doubleLion?: boolean }) {
  return <svg viewBox="0 0 308 128" role="img" aria-label={doubleLion?'獅子が、金に守られた隣の獅子を取り、2回目に別の金に守られた獅子を取ります。先に取る駒が獅子でも付け喰いできます。':'獅子が隣の銀を取り、2回目に金に守られた2マス先の獅子を取ります。銀と獅子を1手で取る付け喰いです。'} data-chu-diagram={doubleLion?'double-lion':'attachment'}>
    {[0,1,2].map(step=><g key={step} data-frame={step} transform={'translate('+(step*106+10)+' 4)'}>
      <Board maxRow={8} pieces={[own(5+step),enemy(8,5,'金'),...(doubleLion?[enemy(7,4,'金')]:[]),...(step===0?[enemy(6,5,doubleLion?'獅':'銀')]:[]),...(step<2?[enemy(7,5,'獅')]:[])]} />
      <text x="38" y="120" textAnchor="middle" fontSize="13">{step===0?'動く前':step===1?(doubleLion?'獅子を取る':'銀を取る'):'獅子を取る'}</text>
    </g>)}
  </svg>;
}

export function LionAttachmentExceptions({ soleProtector = false }: { soleProtector?: boolean }) {
  return <svg viewBox={soleProtector?'0 0 308 151':'0 0 308 174'} role="img" aria-label={soleProtector?'守りが歩兵だけの例と、仲人だけの例です。その歩兵や仲人を先に取っても、2回目に獅子を取ることはできません。どちらも同じ範囲、同じ尺度です。':'先に取る駒が歩兵か仲人の比較です。歩兵や仲人を取る1回目はできますが、守られた獅子を取る2回目はできません。禁止の印は2回目の動きに付いています。'} data-chu-diagram={soleProtector?'sole-protector':'attachment-exceptions'}>
    {[0,1].map(i=><g key={i} data-frame={i} transform={'translate('+(i*154+40)+' 22)'}>
      <text x="38" y="-8" textAnchor="middle" fontSize="13">{soleProtector?(i?'仲人だけが守り':'歩兵だけが守り'):(i?'仲人':'歩兵')}</text>
      <Board minRow={soleProtector?6:5} maxRow={8} pieces={soleProtector?[own(8,6),enemy(7,5,i?'仲':'歩'),enemy(6,5,'獅')]:[own(5),enemy(6,5,i?'仲':'歩'),enemy(7,5,'獅'),enemy(8,5,'金')]}>
        {!soleProtector&&<>
          <path d="M19 83V65m-4 5l4 -5 4 5" fill="none" stroke="#65785d" strokeWidth="1.4" />
          <path d="M19 59V41" fill="none" stroke="#a56a5d" strokeWidth="1.4" />
          <path d="M14 45l10 10M24 45L14 55" stroke="#a56a5d" strokeWidth="1.6" data-forbidden-step="2" />
        </>}
        {soleProtector&&<>
          <path d="M51 38Q73 50 51 62m5 -5l-5 5 7 0" fill="none" stroke="#65785d" strokeWidth="1.4" data-guard />
          <path d="M19 39V59" fill="none" stroke="#a56a5d" strokeWidth="1.4" />
          <path d="M14 45l10 10M24 45L14 55" stroke="#a56a5d" strokeWidth="1.6" data-forbidden-step="2" />
        </>}
      </Board>
      {!soleProtector&&<text x="38" y="123" textAnchor="middle" fontSize="13">{i?'仲人は取れます。':'歩兵は取れます。'}<tspan x="38" dy="22">獅子は取れません。</tspan></text>}
    </g>)}
    {soleProtector&&<text x="154" y="145" textAnchor="middle" fontSize="13">1手で両方取ることはできません。</text>}
  </svg>;
}

export function LionTakenBack() {
  return <svg viewBox="0 0 308 151" role="img" aria-label="銀と獅子を付け喰いした後の盤面です。自分の獅子の隣に相手の金が残っています。相手の次の手で、その金は獅子を取れます。2例の範囲と尺度は同じです。" data-chu-diagram="taken-back">
    {[0,1].map(after=><g key={after} data-frame={after} transform={'translate('+(after*154+40)+' 22)'}>
      <text x="38" y="-8" textAnchor="middle" fontSize="13">{after?'相手の次の手':'銀と獅子を取った後'}</text>
      <Board maxRow={8} pieces={after?[enemy(7,5,'金')]:[own(7),enemy(8,5,'金')]} />
    </g>)}
    <text x="154" y="146" textAnchor="middle" fontSize="13">金が獅子を取ります。</text>
  </svg>;
}
